"use client";

import Image from "next/image";
import { useState } from "react";
import { AnimatePresence, LazyMotion, domAnimation, m, useReducedMotion } from "framer-motion";

import type { HomeDictionary } from "@/content/home";

interface ServiceHubProps {
  copy: HomeDictionary["services"];
}

export default function ServiceHub({ copy }: ServiceHubProps) {
  const [activeId, setActiveId] = useState<string | null>(copy.items[0]?.id ?? null);
  const shouldReduceMotion = useReducedMotion();
  const viewport = { once: true, amount: 0.2 };

  return (
    <LazyMotion features={domAnimation}>
      <section id="soluciones" className="bg-[#F8F9FA] py-24 px-6 lg:px-12 border-t border-gray-200">
        <div className="max-w-[1400px] mx-auto">
          <m.div
            initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 34 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={viewport}
            transition={{ duration: 0.65, ease: "easeOut" }}
            className="flex flex-col mb-20"
          >
            <div className="flex items-center mb-4">
              <div className="w-8 h-[2px] bg-[#E67E22] mr-4" />
              <span className="text-[#E67E22] font-bold text-[10px] uppercase tracking-[0.3em]">
                {copy.eyebrow}
              </span>
            </div>
            <h2 className="font-montserrat font-black text-5xl md:text-8xl text-[#0A111F] uppercase leading-[0.85] tracking-tighter">
              {copy.title} <br /> <span className="text-[#4A5568]/20">{copy.accent}</span>
            </h2>
          </m.div>

          <div className="flex flex-col border-t border-gray-200">
            {copy.items.map((service, index) => {
              const isActive = activeId === service.id;

              return (
                <m.article
                  key={service.id}
                  initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={viewport}
                  transition={{ duration: 0.55, delay: shouldReduceMotion ? 0 : index * 0.07, ease: "easeOut" }}
                  className="relative border-b border-gray-200 overflow-hidden group"
                >
                  <button
                    type="button"
                    className="w-full text-left"
                    onClick={() => setActiveId((current) => (current === service.id ? null : service.id))}
                    onMouseEnter={() => setActiveId(service.id)}
                  >
                    <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between py-10 lg:py-14 relative z-10">
                      <div className="flex items-center gap-6 lg:gap-20">
                        <span className="font-mono text-[#4A5568] text-xs opacity-50">{service.id}</span>
                        <h3 className="font-montserrat font-bold text-3xl md:text-6xl text-[#0A111F] uppercase tracking-tight group-hover:translate-x-4 transition-transform duration-500">
                          {service.title}
                        </h3>
                      </div>
                      <div className="max-w-md mt-6 lg:mt-0 lg:text-right">
                        <p className="font-inter text-[#4A5568] text-base lg:text-lg font-light leading-relaxed group-hover:text-[#0A111F] transition-colors">
                          {service.short}
                        </p>
                      </div>
                    </div>
                  </button>

                  <AnimatePresence initial={false}>
                    {isActive && (
                      <m.div
                        initial={shouldReduceMotion ? { opacity: 1 } : { height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={shouldReduceMotion ? { opacity: 0 } : { height: 0, opacity: 0 }}
                        transition={{ duration: shouldReduceMotion ? 0.15 : 0.55, ease: [0.4, 0, 0.2, 1] }}
                        className="relative z-10 overflow-hidden"
                      >
                        <div className="pt-4 pb-16 lg:pb-20">
                          <div className="flex flex-col lg:flex-row gap-8 lg:gap-16 items-start lg:items-center">
                            <m.div
                              initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, x: -18 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ duration: 0.45, delay: shouldReduceMotion ? 0 : 0.12 }}
                              className="w-full lg:w-1/2 border-l-2 border-[#E67E22] pl-6 lg:pl-8"
                            >
                              <h4 className="font-montserrat font-bold text-lg lg:text-xl text-[#0A111F] mb-6 uppercase tracking-tight">
                                {service.longTitle}
                              </h4>
                              <p className="font-inter text-[#4A5568] text-base lg:text-lg leading-relaxed mb-8 lg:mb-10 font-light">
                                {service.description}
                              </p>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 lg:gap-y-6 gap-x-8 lg:gap-x-12">
                                {service.specs.map((spec) => (
                                  <div key={spec} className="flex items-center group/spec">
                                    <div className="w-1.5 h-1.5 bg-[#E67E22] rounded-full mr-4 group-hover/spec:scale-150 transition-transform" />
                                    <span className="font-montserrat font-bold text-[9px] lg:text-[10px] uppercase tracking-[0.2em] text-[#0A111F]">
                                      {spec}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </m.div>

                            <m.div
                              initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, x: 18, scale: 0.98 }}
                              animate={{ opacity: 1, x: 0, scale: 1 }}
                              transition={{ duration: 0.5, delay: shouldReduceMotion ? 0 : 0.18 }}
                              className="w-full lg:w-1/2 h-[300px] sm:h-[400px] lg:h-[450px] bg-[#0A111F] relative overflow-hidden border border-white/5"
                            >
                              <Image
                                src={service.image}
                                alt={service.title}
                                fill
                                sizes="(max-width: 1024px) 100vw, 50vw"
                                quality={60}
                                className="w-full h-full object-cover object-top grayscale-[60%] brightness-[0.8] contrast-[1.15] sepia-[5%] group-hover:grayscale-0 group-hover:brightness-100 group-hover:scale-105 transition-all duration-1000 ease-in-out"
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-[#0A111F]/90 via-[#0A111F]/30 to-transparent" />
                              <div className="absolute top-4 right-4 border border-white/20 px-2 lg:px-3 py-1 backdrop-blur-md">
                                <span className="font-mono text-[7px] lg:text-[8px] text-white/50 uppercase tracking-[0.2em]">
                                  {copy.scanLabel}: {service.id}
                                </span>
                              </div>
                            </m.div>
                          </div>
                        </div>
                      </m.div>
                    )}
                  </AnimatePresence>

                  <div className="absolute left-0 bottom-0 w-0 h-[3px] bg-[#E67E22] group-hover:w-full transition-all duration-700 ease-out" />
                </m.article>
              );
            })}
          </div>
        </div>
      </section>
    </LazyMotion>
  );
}
